import { Alert, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, List, ListItem, ListItemButton, ListItemIcon, ListItemText, ListSubheader, Snackbar } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Ingredient } from "../../models/Ingredient";
import { ShoppingEntry } from "../../models/ShoppingEntry";
import { addIngredientsToShoppingList } from "../../utils/ShoppingService";

export type AddIngredientsToShoppingListDialogProps = {
    open: boolean;
    setOpen: (open: boolean) => void;
    ingredients: Ingredient[];
    factor: number;
}

const AddIngredientsToShoppingListDialog = (props: AddIngredientsToShoppingListDialogProps) => {
    const {t} = useTranslation();
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
    const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

    useEffect(() => {
        if(props.open) {
            setSelectedIds(props.ingredients.filter(ingredient => ingredient.id !== null).map(ingredient => ingredient.id as number));
        }
    }, [props.open, props.ingredients]);

    const toggleIngredient = useCallback((ingredientId: number) => {
        setSelectedIds(currentSelectedIds => {
            if(currentSelectedIds.includes(ingredientId)) {
                return currentSelectedIds.filter(id => id !== ingredientId);
            }
            return [...currentSelectedIds, ingredientId];
        });
    }, []);

    const selectAll = useCallback(() => {
        setSelectedIds(props.ingredients.filter(ingredient => ingredient.id !== null).map(ingredient => ingredient.id as number));
    }, [props.ingredients]);

    const selectNone = useCallback(() => {
        setSelectedIds([]);
    }, []);

    const groupedIngredients = useMemo(() => {
        const map = new Map<string, Ingredient[]>();
        [...props.ingredients].sort((i1, i2) => i1.orderNumber - i2.orderNumber).forEach((ingredient: Ingredient) => {
            const key: string = ingredient.group ? ingredient.group : '';
            const collection = map.get(key);
            if(!collection) {
                map.set(key, [ingredient]);
            } else {
                collection.push(ingredient);
            }
        });
        return map;
    }, [props.ingredients]);

    const confirm = useCallback(() => {
        const entries: ShoppingEntry[] = props.ingredients
            .filter(ingredient => ingredient.id !== null && selectedIds.includes(ingredient.id))
            .map(ingredient => {
                return {
                    id: null,
                    name: ingredient.name,
                    amount: ingredient.amount != null ? props.factor * ingredient.amount : undefined,
                    unit: ingredient.unit
                } as ShoppingEntry;
            });

        addIngredientsToShoppingList(entries).then(() => {
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
            props.setOpen(false);
        }).catch(() => {
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        });
    }, [props, selectedIds]);

    return (
        <>
            <Dialog open={props.open} onClose={() => props.setOpen(false)}>
                <DialogTitle>{t('addToShoppingListTitle')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('addToShoppingListContent')}
                    </DialogContentText>
                    <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', marginTop: '5px'}}>
                        <Button size="small" onClick={selectAll}>{t('selectAll')}</Button>
                        <Button size="small" onClick={selectNone}>{t('selectNone')}</Button>
                    </div>
                    <List style={{maxHeight: '320px', minWidth: '280px', overflowY: 'auto'}}>
                        {Array.from(groupedIngredients.keys()).map((group: string) => {
                            return (
                                <li key={`section-${group}`}>
                                    <ul style={{padding: 0}}>
                                        {group && <ListSubheader>{group}</ListSubheader>}
                                        {groupedIngredients.get(group)?.map((ingredient: Ingredient) => {
                                            const amountUnit: string | null = ingredient.amount != null
                                                ? (props.factor * ingredient.amount) + (ingredient.unit ? ' ' + ingredient.unit : '')
                                                : null;
                                            const checked: boolean = ingredient.id !== null && selectedIds.includes(ingredient.id);
                                            return (
                                                <ListItem disablePadding key={ingredient.id ?? ingredient.name}>
                                                    <ListItemButton role={undefined} onClick={() => ingredient.id !== null && toggleIngredient(ingredient.id)} dense>
                                                        <ListItemIcon>
                                                            <Checkbox edge="start" checked={checked} tabIndex={-1} disableRipple />
                                                        </ListItemIcon>
                                                        <ListItemText primary={ingredient.name} secondary={amountUnit} />
                                                    </ListItemButton>
                                                </ListItem>
                                            );
                                        })}
                                    </ul>
                                </li>
                            );
                        })}
                    </List>
                </DialogContent>
                <DialogActions>
                    <Button onClick={confirm} variant="contained" disabled={selectedIds.length === 0}>{t('addToShoppingList')}</Button>
                    <Button onClick={() => props.setOpen(false)}>{t('cancel')}</Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                anchorOrigin={{vertical: 'top', horizontal: 'center'}}
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={() => setSnackbarOpen(false)}
            >
                <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} variant="filled" sx={{width: '100%'}}>
                    {snackbarSeverity === 'success' ? t('addedToShoppingList') : t('couldNotAddToShoppingList')}
                </Alert>
            </Snackbar>
        </>
    );
}

export default AddIngredientsToShoppingListDialog;
